// LQC-2172 - pure logic behind the OP's edit-expiry modal: who sees the pencil,
// what the modal opens with, and when Update becomes active.

import { ExecutionStrategyStatus } from "@/lib/interfaces and Enums/ENUMExecutionStrategyStatus";
import { parseServerDateTime } from "@/lib/globalFormatterHelper";
import {
  DEFAULT_EXPIRY_PERIOD_SECONDS,
  EXPIRY_MAX_SECONDS,
  EXPIRY_MIN_SECONDS,
  EXPIRY_PERIOD_TYPE_SECONDS,
} from "../../../tradeSettingsHelper/expiryDurationHelper";
import { IToggleButton } from "../../../tradeSettingsHelper/tradeSettingsInterfaces";
import { defaultOrderExpiryTypeOptions } from "../../../tradeSettingsHelper/toggleHandler";

export const EXPIRY_TYPE_TIME = "Time";
export const EXPIRY_TYPE_GTC = "GTC";

export interface IExpiryEditState {
  expiryType: string;
  // Total seconds. Only meaningful while the type is "Time".
  expiryTimeValue?: number;
}

// Statuses where extending is off the table (LQC-2366): nothing left to extend,
// or nothing sent yet - a New trade's expiry belongs to the settings panel.
// A deny list, not a positive one: the backend accepts an extend against
// anything else, `Expired` included, so a new status gets the pencil.
export const STATUSES_WITHOUT_EXPIRY_EDIT: string[] = [
  ExecutionStrategyStatus.New,
  // Done trades have no expiry left to push out (LQC-2678).
  ExecutionStrategyStatus.Executed,
  ExecutionStrategyStatus.Filled,
  ExecutionStrategyStatus.NoMoreFills,
  ExecutionStrategyStatus.Affirmed,
  ExecutionStrategyStatus.ReCreated,
  ExecutionStrategyStatus.Cancelled,
  ExecutionStrategyStatus.NotDealt,
];

// Only the OP owns the trade's expiry, so only the OP's row gets the pencil.
export function canEditTradeExpiry(
  role: string | undefined,
  flagOn: boolean,
  status?: string,
): boolean {
  if (!flagOn) return false;
  if (role !== "OP") return false;
  return !STATUSES_WITHOUT_EXPIRY_EDIT.includes(status ?? "");
}

export function clampExpirySeconds(seconds: number): number {
  if (seconds < EXPIRY_MIN_SECONDS) return EXPIRY_MIN_SECONDS;
  if (seconds > EXPIRY_MAX_SECONDS) return EXPIRY_MAX_SECONDS;
  return seconds;
}

// Seed the modal from the trade's own expiryEnd: a timestamp means the trade
// already expires, so open on "Time" showing what is left (capped at the 7d
// input maximum). No timestamp means GTC.
export function deriveInitialExpiryState(
  expiryEnd: string | number | Date | null | undefined,
  nowMs: number,
): IExpiryEditState {
  // parseServerDateTime, not new Date: expiryEnd arrives without a timezone
  // designator, so a raw parse seeds the popover hours out.
  const parsed = parseServerDateTime(expiryEnd);
  if (!parsed) return { expiryType: EXPIRY_TYPE_GTC };

  const remainingSeconds = Math.round((parsed.getTime() - nowMs) / 1000);
  // A lapsed trade has no remaining time worth showing, so open on the same
  // default a new trade starts on rather than the unhelpful 30s floor.
  if (remainingSeconds < EXPIRY_MIN_SECONDS) {
    return {
      expiryType: EXPIRY_TYPE_TIME,
      expiryTimeValue: DEFAULT_EXPIRY_PERIOD_SECONDS,
    };
  }

  return {
    expiryType: EXPIRY_TYPE_TIME,
    expiryTimeValue: clampExpirySeconds(remainingSeconds),
  };
}

// Update is live whenever there is something to send, with no "must have
// changed" rule (LQC-2366): the trade is counting down, so the duration on
// screen is always more than what is actually left, and submitting it unchanged
// genuinely pushes the expiry back out.
export function canSubmitExpiryEdit(current: IExpiryEditState): boolean {
  if (current.expiryType !== EXPIRY_TYPE_TIME) return true;
  return !!current.expiryTimeValue && current.expiryTimeValue > 0;
}

// The same GTC/Day/Time options the New trade panel offers, with the draft
// selection marked active (SettingDropdown reads `active`).
export function buildExpiryTypeOptions(activeName: string): IToggleButton[] {
  return defaultOrderExpiryTypeOptions.map((option) => ({
    ...option,
    active: option.name === activeName,
  }));
}

// The popover opens anchored to the pencil, so its size has to be known before
// it renders in order to flip it away from a viewport edge.
export const EXPIRY_POPOVER_WIDTH = 320;
// Includes the row reserved for a rejection message under the duration input.
export const EXPIRY_POPOVER_HEIGHT = 170;
const VIEWPORT_MARGIN = 8;
const ANCHOR_GAP = 4;

export interface IAnchorRect {
  top: number;
  left: number;
  bottom: number;
  right: number;
}

// Place the popover below-right of the pencil, flipping above/left when it
// would otherwise run off screen - the blotter's bottom rows and rightmost
// columns are the common case.
export function calculateExpiryPopoverPosition(
  anchor: IAnchorRect,
  viewport: { width: number; height: number },
  size: { width: number; height: number } = {
    width: EXPIRY_POPOVER_WIDTH,
    height: EXPIRY_POPOVER_HEIGHT,
  },
): { top: number; left: number } {
  let left = anchor.left;
  if (left + size.width > viewport.width - VIEWPORT_MARGIN) {
    left = anchor.right - size.width;
  }

  let top = anchor.bottom + ANCHOR_GAP;
  if (top + size.height > viewport.height - VIEWPORT_MARGIN) {
    top = anchor.top - size.height - ANCHOR_GAP;
  }

  return {
    top: Math.max(VIEWPORT_MARGIN, top),
    left: Math.max(VIEWPORT_MARGIN, left),
  };
}

// PUT /Trade/Execution/Edit/Expiry (LQC-2166). Same expiry triple the New/Edit
// panel sends, including the period on GTC/Day: the backend ignores it there and
// stamps its own 7 days, and dropping a required field risks a 400.
export function buildExpiryUpdatePayload(
  esid: string,
  current: IExpiryEditState,
) {
  return {
    esid,
    expiryType: current.expiryType,
    expiryPeriod: current.expiryTimeValue ?? DEFAULT_EXPIRY_PERIOD_SECONDS,
    expiryPeriodType: EXPIRY_PERIOD_TYPE_SECONDS,
  };
}
